import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Plus, Users, Building2, Pencil, Trash2, UserPlus, Loader2,
    RotateCcw, Power, PowerOff, Key, AlertTriangle, ShieldOff
} from 'lucide-react';
import ModuleBg from '../components/ModuleBg';
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', admin_agence: 'Admin Agence', user: 'Commercial', responsable: 'Gérant', assistant: 'Secrétariat' };
const ROLE_COLORS = { super_admin: 'bg-red-50 text-red-600', admin: 'bg-[#6366F1]/10 text-[#818CF8]', admin_agence: 'bg-[#6366F1]/10 text-[#818CF8]', user: 'bg-gray-100 text-gray-500' };

export default function AdminPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [users, setUsers] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [showAgencyDialog, setShowAgencyDialog] = useState(false);
    const [showResetPwDialog, setShowResetPwDialog] = useState(false);
    const [showBulkDialog, setShowBulkDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editingAgency, setEditingAgency] = useState(null);
    const [resetPwUser, setResetPwUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'user', agency_id: '' });
    const [agencyForm, setAgencyForm] = useState({ name: '', address: '', phone: '' });
    const [userFilter, setUserFilter] = useState('active');
    const [appSettings, setAppSettings] = useState({ public_registration_enabled: false });
    const [accessRequests, setAccessRequests] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const promises = [
                axios.get(`${API_URL}/api/users`, { withCredentials: true }),
                axios.get(`${API_URL}/api/agencies`, { withCredentials: true })
            ];
            if (user?.role === 'super_admin') {
                promises.push(axios.get(`${API_URL}/api/app-settings`, { withCredentials: true }));
                promises.push(axios.get(`${API_URL}/api/access-requests`, { withCredentials: true }));
            }
            const results = await Promise.all(promises);
            setUsers(results[0].data);
            setAgencies(results[1].data);
            if (results[2]) setAppSettings(results[2].data);
            if (results[3]) setAccessRequests(results[3].data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [user?.role]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Filter users
    const filteredUsers = users.filter(u => {
        if (userFilter === 'active') return u.active !== false;
        if (userFilter === 'inactive') return u.active === false;
        return true;
    });
    const activeCount = users.filter(u => u.active !== false).length;
    const inactiveCount = users.filter(u => u.active === false).length;

    const togglePublicReg = async () => {
        const newVal = !appSettings.public_registration_enabled;
        try {
            await axios.put(`${API_URL}/api/app-settings`, { public_registration_enabled: newVal }, { withCredentials: true });
            setAppSettings({ ...appSettings, public_registration_enabled: newVal });
            toast.success(newVal ? 'Inscriptions publiques activées' : 'Inscriptions publiques désactivées (bêta privée)');
        } catch { toast.error('Erreur'); }
    };

    const setAgencyStatus = async (agencyId, status) => {
        try {
            await axios.put(`${API_URL}/api/agencies/${agencyId}/status`, { status }, { withCredentials: true });
            toast.success(`Agence ${status === 'active' ? 'activée' : status === 'disabled' ? 'désactivée' : status}`);
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const updateAccessReqStatus = async (reqId, statut) => {
        try {
            await axios.put(`${API_URL}/api/access-requests/${reqId}`, { statut }, { withCredentials: true });
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const deleteAccessReq = async (reqId) => {
        try {
            await axios.delete(`${API_URL}/api/access-requests/${reqId}`, { withCredentials: true });
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const saveUser = async () => {
        try {
            if (editingUser) {
                await axios.put(`${API_URL}/api/users/${editingUser.id}`, {
                    name: userForm.name, role: userForm.role, agency_id: userForm.agency_id || null,
                    gerant: userForm.role === 'user' ? !!userForm.gerant : false, peut_valider: userForm.role === 'assistant' ? !!userForm.peut_valider : false
                }, { withCredentials: true });
                toast.success('Utilisateur mis à jour');
            } else {
                await axios.post(`${API_URL}/api/users`, userForm, { withCredentials: true });
                toast.success('Utilisateur créé');
            }
            setShowUserDialog(false);
            setEditingUser(null);
            fetchData();
        } catch (e) {
            const detail = e.response?.data?.detail || 'Erreur';
            toast.error(detail);
        }
    };

    const saveAgency = async () => {
        try {
            if (editingAgency) {
                await axios.put(`${API_URL}/api/agencies/${editingAgency.id}`, agencyForm, { withCredentials: true });
                toast.success('Agence mise à jour');
            } else {
                await axios.post(`${API_URL}/api/agencies`, agencyForm, { withCredentials: true });
                toast.success('Agence créée');
            }
            setShowAgencyDialog(false);
            setEditingAgency(null);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const deactivateUser = async (id) => {
        if (!window.confirm('Désactiver cet utilisateur ? Il ne pourra plus se connecter.')) return;
        try {
            await axios.delete(`${API_URL}/api/users/${id}`, { withCredentials: true });
            toast.success('Utilisateur désactivé');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const reactivateUser = async (id) => {
        try {
            await axios.post(`${API_URL}/api/users/${id}/reactivate`, {}, { withCredentials: true });
            toast.success('Utilisateur réactivé');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const deletePermanent = async (id, name) => {
        if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT "${name}" ? Cette action est irréversible.`)) return;
        try {
            await axios.delete(`${API_URL}/api/users/${id}/permanent`, { withCredentials: true });
            toast.success('Utilisateur supprimé définitivement');
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const resetPassword = async () => {
        if (!resetPwUser || !newPassword) return;
        try {
            await axios.post(`${API_URL}/api/users/${resetPwUser.id}/reset-password`, { password: newPassword }, { withCredentials: true });
            toast.success(`Mot de passe réinitialisé pour ${resetPwUser.name}`);
            setShowResetPwDialog(false);
            setResetPwUser(null);
            setNewPassword('');
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const bulkDeactivate = async () => {
        try {
            const { data } = await axios.post(`${API_URL}/api/users/bulk-deactivate`, {}, { withCredentials: true });
            toast.success(`${data.count} utilisateur(s) désactivé(s)`);
            setShowBulkDialog(false);
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const deleteAgency = async (id) => {
        if (!window.confirm('Supprimer cette agence ?')) return;
        try {
            await axios.delete(`${API_URL}/api/agencies/${id}`, { withCredentials: true });
            toast.success('Agence supprimée');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const openEditUser = (u) => {
        setEditingUser(u);
        setUserForm({ email: u.email, password: '', name: u.name, role: u.role, agency_id: u.agency_id || '', gerant: !!u.gerant, peut_valider: !!u.peut_valider });
        setShowUserDialog(true);
    };

    const openNewUser = () => {
        setEditingUser(null);
        setUserForm({ email: '', password: '', name: '', role: 'user', agency_id: '' });
        setShowUserDialog(true);
    };

    const openEditAgency = (a) => {
        setEditingAgency(a);
        setAgencyForm({ name: a.name, address: a.address, phone: a.phone });
        setShowAgencyDialog(true);
    };

    const openNewAgency = () => {
        setEditingAgency(null);
        setAgencyForm({ name: '', address: '', phone: '' });
        setShowAgencyDialog(true);
    };

    const openResetPw = (u) => {
        setResetPwUser(u);
        setNewPassword('');
        setShowResetPwDialog(true);
    };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <ModuleBg>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-page">
            <h1 className="text-xl font-semibold font-['Inter'] text-gray-800 tracking-tight mb-6">Administration</h1>

            <Tabs defaultValue="users" className="space-y-6">
                <TabsList className="bg-white border border-gray-200">
                    <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 text-gray-400">
                        <Users className="w-4 h-4 mr-2" strokeWidth={1.5} />Utilisateurs
                    </TabsTrigger>
                    <TabsTrigger value="agencies" data-testid="tab-agencies" className="data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 text-gray-400">
                        <Building2 className="w-4 h-4 mr-2" strokeWidth={1.5} />Agences
                    </TabsTrigger>
                    {isSuperAdmin && <TabsTrigger value="access" data-testid="tab-access" className="data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 text-gray-400">
                        <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />Demandes d'accès{accessRequests.filter(r => r.statut === 'nouveau').length > 0 && <span className="ml-1.5 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{accessRequests.filter(r => r.statut === 'nouveau').length}</span>}
                    </TabsTrigger>}
                    {isSuperAdmin && <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 text-gray-400">
                        <Power className="w-4 h-4 mr-2" strokeWidth={1.5} />Paramètres
                    </TabsTrigger>}
                </TabsList>

                {/* USERS TAB */}
                <TabsContent value="users" className="space-y-4">
                    {/* Header with filters */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-2">
                            <button onClick={() => setUserFilter('active')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${userFilter === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400 hover:text-gray-600'}`} data-testid="filter-active">
                                Actifs ({activeCount})
                            </button>
                            <button onClick={() => setUserFilter('inactive')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${userFilter === 'inactive' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-400 hover:text-gray-600'}`} data-testid="filter-inactive">
                                Désactivés ({inactiveCount})
                            </button>
                            <button onClick={() => setUserFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${userFilter === 'all' ? 'bg-[#6366F1]/10 text-[#818CF8]' : 'bg-gray-100 text-gray-400 hover:text-gray-600'}`}>
                                Tous ({users.length})
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            {user?.role === 'super_admin' && (
                                <Button variant="ghost" onClick={() => setShowBulkDialog(true)} className="text-red-600 hover:text-red-300 hover:bg-red-50 text-xs" data-testid="bulk-deactivate-btn">
                                    <ShieldOff className="w-4 h-4 mr-1" />Désactiver tous sauf admin
                                </Button>
                            )}
                            <Button onClick={openNewUser} className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900" data-testid="add-user-btn">
                                <UserPlus className="w-4 h-4 mr-2" />Nouvel utilisateur
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {filteredUsers.map(u => (
                            <div key={u.id} className={`card-base p-4 flex items-center justify-between ${u.active === false ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-gray-900 truncate">{u.name}</span>
                                        <Badge className={`text-xs ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                                            {ROLE_LABELS[u.role] || u.role}
                                        </Badge>
                                        {u.gerant && <Badge className="text-xs bg-emerald-50 text-emerald-700" data-testid={`user-gerant-${u.id}`}>+ droits gérant</Badge>}
                                        {u.peut_valider && <Badge className="text-xs bg-sky-50 text-sky-700">peut valider</Badge>}
                                        {u.active === false && <Badge className="text-xs bg-red-50 text-red-600">Désactivé</Badge>}
                                    </div>
                                    <p className="text-xs text-gray-400 truncate">{u.email} {u.agency_name ? `— ${u.agency_name}` : ''}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    {u.active === false ? (
                                        <>
                                            <button onClick={() => reactivateUser(u.id)} className="p-2 hover:bg-emerald-50 rounded-lg" title="Réactiver" data-testid={`reactivate-${u.id}`}>
                                                <Power className="w-4 h-4 text-emerald-600" />
                                            </button>
                                            {user?.role === 'super_admin' && (
                                                <button onClick={() => deletePermanent(u.id, u.name)} className="p-2 hover:bg-red-50 rounded-lg" title="Supprimer définitivement">
                                                    <Trash2 className="w-4 h-4 text-red-600" />
                                                </button>
                                            )}
                                        </>
                                    ) : (isSuperAdmin || (!['super_admin', 'admin'].includes(u.role) && u.id !== user?.id)) && (
                                        <>
                                            <button onClick={() => openEditUser(u)} className="p-2 hover:bg-white/10 rounded-lg" title="Modifier" data-testid={`edit-user-${u.id}`}>
                                                <Pencil className="w-4 h-4 text-gray-500" />
                                            </button>
                                            <button onClick={() => openResetPw(u)} className="p-2 hover:bg-white/10 rounded-lg" title="Réinitialiser mot de passe">
                                                <Key className="w-4 h-4 text-gray-500" />
                                            </button>
                                            {u.role !== 'super_admin' && (
                                                <button onClick={() => deactivateUser(u.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Désactiver" data-testid={`deactivate-${u.id}`}>
                                                    <PowerOff className="w-4 h-4 text-red-600" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                        {filteredUsers.length === 0 && <p className="text-center text-gray-400 py-10">Aucun utilisateur {userFilter === 'inactive' ? 'désactivé' : ''}</p>}
                    </div>
                </TabsContent>

                {/* AGENCIES TAB */}
                <TabsContent value="agencies" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-gray-500 text-sm">{agencies.length} agence(s)</p>
                        {user?.role === 'super_admin' && (
                            <Button onClick={openNewAgency} className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900" data-testid="add-agency-btn">
                                <Plus className="w-4 h-4 mr-2" />Nouvelle agence
                            </Button>
                        )}
                    </div>
                    <div className="space-y-2">
                        {agencies.map(a => (
                            <div key={a.id} className="card-base p-4 flex items-center justify-between" data-testid={`agency-row-${a.id}`}>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900">{a.name}</p>
                                        {isSuperAdmin && (
                                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                                                a.status === 'disabled' ? 'bg-red-50 text-red-600 border-red-200' :
                                                a.status === 'pending' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                                'bg-emerald-50 text-emerald-600 border-emerald-200'
                                            }`}>{a.status === 'disabled' ? 'Désactivée' : a.status === 'pending' ? 'En attente' : 'Active'}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400">
                                        {a.responsable ? `${a.responsable} · ` : ''}{a.email_responsable || ''}
                                        {a.address ? ` · ${a.address}` : ''}{a.phone ? ` · ${a.phone}` : ''}
                                    </p>
                                    <p className="text-xs text-[#6366F1] mt-1">
                                        {a.seuil != null ? `Seuil: ${a.seuil.toLocaleString('fr-FR')} € | Taux bas: ${a.taux_bas}% | Taux haut: ${a.taux_haut}%` : 'Paramètres globaux'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {isSuperAdmin && a.status !== 'disabled' && (
                                        <button onClick={() => setAgencyStatus(a.id, 'disabled')} className="p-2 hover:bg-red-50 rounded-lg" title="Désactiver" data-testid={`disable-agency-${a.id}`}>
                                            <PowerOff className="w-4 h-4 text-red-400" />
                                        </button>
                                    )}
                                    {isSuperAdmin && a.status === 'disabled' && (
                                        <button onClick={() => setAgencyStatus(a.id, 'active')} className="p-2 hover:bg-emerald-50 rounded-lg" title="Réactiver" data-testid={`enable-agency-${a.id}`}>
                                            <Power className="w-4 h-4 text-emerald-500" />
                                        </button>
                                    )}
                                    <button onClick={() => openEditAgency(a)} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`edit-agency-${a.id}`}>
                                        <Pencil className="w-4 h-4 text-gray-500" />
                                    </button>
                                    {isSuperAdmin && (
                                        <button onClick={() => deleteAgency(a.id)} className="p-2 hover:bg-red-50 rounded-lg">
                                            <Trash2 className="w-4 h-4 text-red-600" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {agencies.length === 0 && <p className="text-center text-gray-400 py-10">Aucune agence</p>}
                    </div>
                </TabsContent>

                {/* ACCESS REQUESTS TAB - Super Admin only */}
                {isSuperAdmin && (
                    <TabsContent value="access" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Demandes d'accès</h3>
                            <p className="text-sm text-gray-400">{accessRequests.length} demande{accessRequests.length > 1 ? 's' : ''}</p>
                        </div>
                        <div className="space-y-2">
                            {accessRequests.map(r => {
                                const stColors = { nouveau: 'bg-blue-50 text-blue-600 border-blue-200', contacte: 'bg-amber-50 text-amber-600 border-amber-200', accepte: 'bg-emerald-50 text-emerald-600 border-emerald-200', refuse: 'bg-red-50 text-red-600 border-red-200' };
                                const stLabels = { nouveau: 'Nouveau', contacte: 'Contacté', accepte: 'Accepté', refuse: 'Refusé' };
                                return (
                                    <div key={r.id} className="card-base p-4" data-testid={`access-req-${r.id}`}>
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="font-medium text-gray-900">{r.nom}</p>
                                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${stColors[r.statut] || stColors.nouveau}`}>{stLabels[r.statut] || r.statut}</span>
                                                </div>
                                                <p className="text-sm text-gray-600">{r.entreprise}</p>
                                                <p className="text-xs text-gray-400">{r.email}{r.telephone ? ` · ${r.telephone}` : ''}</p>
                                                {r.message && <p className="text-xs text-gray-500 mt-1 italic">"{r.message}"</p>}
                                                <p className="text-[10px] text-gray-300 mt-1">{r.created_at?.slice(0, 10)}</p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {r.statut === 'nouveau' && <Button size="sm" variant="outline" onClick={() => updateAccessReqStatus(r.id, 'contacte')} className="text-xs h-7">Contacté</Button>}
                                                {r.statut !== 'accepte' && <Button size="sm" onClick={() => updateAccessReqStatus(r.id, 'accepte')} className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white">Accepter</Button>}
                                                {r.statut !== 'refuse' && <Button size="sm" variant="outline" onClick={() => updateAccessReqStatus(r.id, 'refuse')} className="text-xs h-7 text-red-500 border-red-200 hover:bg-red-50">Refuser</Button>}
                                                <button onClick={() => deleteAccessReq(r.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-300" /></button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {accessRequests.length === 0 && <p className="text-center text-gray-400 py-10">Aucune demande d'accès</p>}
                        </div>
                    </TabsContent>
                )}

                {/* SETTINGS TAB - Super Admin only */}
                {isSuperAdmin && (
                    <TabsContent value="settings" className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900">Paramètres globaux</h3>
                        <div className="card-base p-6 max-w-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-900">Inscriptions publiques</p>
                                    <p className="text-sm text-gray-400 mt-0.5">{appSettings.public_registration_enabled ? "Les visiteurs peuvent créer librement une agence" : "Bêta privée — Accès sur invitation uniquement"}</p>
                                </div>
                                <button
                                    onClick={togglePublicReg}
                                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${appSettings.public_registration_enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                    data-testid="toggle-public-reg"
                                >
                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${appSettings.public_registration_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        </div>
                    </TabsContent>
                )}
            </Tabs>

            {/* USER DIALOG */}
            <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                <DialogContent className="bg-white border-gray-200 text-gray-900">
                    <DialogHeader>
                        <DialogTitle>{editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!editingUser && (
                            <>
                                <div className="space-y-2">
                                    <Label className="text-gray-500">Email</Label>
                                    <Input value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} className="bg-white border-gray-200" data-testid="user-form-email" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-500">Mot de passe</Label>
                                    <Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} className="bg-white border-gray-200" data-testid="user-form-password" />
                                </div>
                            </>
                        )}
                        <div className="space-y-2">
                            <Label className="text-gray-500">Nom</Label>
                            <Input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} className="bg-white border-gray-200" data-testid="user-form-name" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-500">Rôle</Label>
                            <Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}>
                                <SelectTrigger className="bg-white border-gray-200" data-testid="user-form-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200">
                                    <SelectItem value="user">Commercial</SelectItem>
                                    <SelectItem value="assistant">Secrétariat</SelectItem>
                                    <SelectItem value="responsable">Gérant</SelectItem>
                                    <SelectItem value="admin_agence">Admin Agence</SelectItem>
                                    {user?.role === 'super_admin' && <SelectItem value="admin">Admin</SelectItem>}
                                    {user?.role === 'super_admin' && <SelectItem value="super_admin">Super Admin</SelectItem>}
                                </SelectContent>
                            </Select>
                            {editingUser && userForm.role === 'user' && (
                                <label className="flex items-start gap-2 text-[12px] text-gray-600 pt-1"><input type="checkbox" checked={!!userForm.gerant} onChange={e => setUserForm({...userForm, gerant: e.target.checked})} data-testid="user-form-gerant" className="mt-0.5" />
                                    Droits de gérant (gestion de l'agence) — reste commercial : ses véhicules et commissions ne changent pas</label>
                            )}
                            {editingUser && userForm.role === 'assistant' && (
                                <label className="flex items-start gap-2 text-[12px] text-gray-600 pt-1"><input type="checkbox" checked={!!userForm.peut_valider} onChange={e => setUserForm({...userForm, peut_valider: e.target.checked})} data-testid="user-form-peut-valider" className="mt-0.5" />
                                    Peut valider définitivement les factures (sinon : préparation, validée par un gérant)</label>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-500">Agence</Label>
                            <Select value={userForm.agency_id || 'none'} onValueChange={v => setUserForm({...userForm, agency_id: v === 'none' ? '' : v})}>
                                <SelectTrigger className="bg-white border-gray-200" data-testid="user-form-agency">
                                    <SelectValue placeholder="Aucune" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200">
                                    <SelectItem value="none">Aucune</SelectItem>
                                    {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowUserDialog(false)} className="text-gray-500">Annuler</Button>
                        <Button onClick={saveUser} className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900" data-testid="user-form-submit">
                            {editingUser ? 'Mettre à jour' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AGENCY DIALOG */}
            <Dialog open={showAgencyDialog} onOpenChange={setShowAgencyDialog}>
                <DialogContent className="bg-white border-gray-200 text-gray-900">
                    <DialogHeader>
                        <DialogTitle>{editingAgency ? "Modifier l'agence" : 'Nouvelle agence'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-gray-500">Nom</Label>
                            <Input value={agencyForm.name} onChange={e => setAgencyForm({...agencyForm, name: e.target.value})} className="bg-white border-gray-200" data-testid="agency-form-name" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-500">Adresse</Label>
                            <Input value={agencyForm.address} onChange={e => setAgencyForm({...agencyForm, address: e.target.value})} className="bg-white border-gray-200" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-500">Téléphone</Label>
                            <Input value={agencyForm.phone} onChange={e => setAgencyForm({...agencyForm, phone: e.target.value})} className="bg-white border-gray-200" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowAgencyDialog(false)} className="text-gray-500">Annuler</Button>
                        <Button onClick={saveAgency} className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900" data-testid="agency-form-submit">
                            {editingAgency ? 'Mettre à jour' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RESET PASSWORD DIALOG */}
            <Dialog open={showResetPwDialog} onOpenChange={setShowResetPwDialog}>
                <DialogContent className="bg-white border-gray-200 text-gray-900">
                    <DialogHeader>
                        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-500">Nouveau mot de passe pour <span className="text-gray-900 font-medium">{resetPwUser?.name}</span> ({resetPwUser?.email})</p>
                        <Input type="password" placeholder="Nouveau mot de passe (min. 6 caractères)" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="bg-white border-gray-200" data-testid="reset-pw-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowResetPwDialog(false)} className="text-gray-500">Annuler</Button>
                        <Button onClick={resetPassword} disabled={newPassword.length < 6} className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900" data-testid="reset-pw-submit">Réinitialiser</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* BULK DEACTIVATE DIALOG */}
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
                <DialogContent className="bg-white border-gray-200 text-gray-900">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" />Désactivation massive</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-gray-500">
                        Cette action va <span className="text-red-600 font-medium">désactiver tous les utilisateurs</span> sauf les super admins et admins.
                        Les comptes désactivés ne pourront plus se connecter mais leurs données seront conservées.
                    </p>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowBulkDialog(false)} className="text-gray-500">Annuler</Button>
                        <Button onClick={bulkDeactivate} className="bg-red-600 hover:bg-red-700 text-gray-900" data-testid="confirm-bulk-deactivate">Désactiver tous</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </ModuleBg>
    );
}
